import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { EMPTY, map, Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import type {
  OwnPlayerProfile,
  OwnPlayerProfileInput,
  PlayersDirectoryQuery,
  PlayersDirectoryResponse,
  PlayRequestAction,
  PlayRequestActionResult,
  PlayRequestItem,
  PlayRequestsQuery,
  PlayRequestsResponse,
  SendPlayRequestBody,
} from './players.model';

/**
 * Cliente de la API de jugadores (B11, `docs/api/players-api.md`). Todas las rutas son
 * `auth:api`: el Bearer de la sesión lo pone el `authInterceptor`.
 *
 * **Nunca en el servidor** (B30, D4): los datos del directorio son de personas con
 * cuenta y `/comunidad` se renderiza en SSR; si algo pidiera en el servidor, el
 * observable completa sin petición. Sin `TransferState` ni caché (B16): retirarse o
 * borrarse surte efecto en la siguiente petición.
 *
 * `HttpClient` directo y no `ApiService`: aquí un 403 o un 401 son estados esperados de
 * la sección y `ApiService` los registraría como errores.
 */
@Injectable({ providedIn: 'root' })
export class PlayersService {
  private readonly http = inject(HttpClient);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly base = `${environment.apiUrl}/players`;

  /**
   * `GET /players?game=&province=&page=`. «Todos» / «Todas» (`null`) no se envían y
   * `per_page` tampoco (el del servidor, 24).
   */
  getDirectory(query: PlayersDirectoryQuery): Observable<PlayersDirectoryResponse> {
    if (!this.isBrowser) return EMPTY;
    let params = new HttpParams();
    if (query.game) params = params.set('game', query.game);
    if (query.province) params = params.set('province', query.province);
    params = params.set('page', String(query.page));
    return this.http.get<PlayersDirectoryResponse>(this.base, { params });
  }

  /** `GET /players/me` → el perfil propio o `null` si aún no tiene (no es un error). */
  getMe(): Observable<OwnPlayerProfile | null> {
    if (!this.isBrowser) return EMPTY;
    return this.http
      .get<{ data: OwnPlayerProfile | null }>(`${this.base}/me`)
      .pipe(map((body) => body?.data ?? null));
  }

  /**
   * `PUT /players/me` (alta o sustitución completa, D18): el cuerpo viaja tal cual, sin
   * quitar nada. Devuelve el perfil ya normalizado por la API (alias en minúsculas,
   * WhatsApp en `+506…`). Los 422 llegan como `{"error": {"<clave>": ["<copy>"]}}`.
   */
  saveMe(body: OwnPlayerProfileInput): Observable<OwnPlayerProfile> {
    if (!this.isBrowser) return EMPTY;
    return this.http
      .put<{ data: OwnPlayerProfile }>(`${this.base}/me`, body)
      .pipe(map((res) => res.data));
  }

  /**
   * `DELETE /players/me` (B19, borrado de verdad). Emite el texto del servidor
   * (`PROFILE_DELETED`) o `null` si el cuerpo no trae una cadena: la pestaña lo pinta.
   */
  deleteMe(): Observable<string | null> {
    if (!this.isBrowser) return EMPTY;
    return this.http
      .delete<{ data?: unknown }>(`${this.base}/me`)
      .pipe(map((res) => (typeof res?.data === 'string' && res.data.trim() !== '' ? res.data : null)));
  }

  // ── Solicitudes (U2, J3) ─────────────────────────────────────────────────

  /**
   * `POST /players/{alias}/requests`. El alias va codificado. `game`, `message` y `website`
   * (honeypot) solo viajan si traen valor. Emite el `message` del servidor (`REQUEST_SENT`).
   */
  sendRequest(alias: string, body: SendPlayRequestBody): Observable<string | null> {
    if (!this.isBrowser) return EMPTY;
    const payload: SendPlayRequestBody = {};
    if (body.game) payload.game = body.game;
    if (body.message) payload.message = body.message;
    if (body.website) payload.website = body.website;
    return this.http
      .post<{ data?: { message?: unknown } }>(`${this.base}/${encodeURIComponent(alias)}/requests`, payload)
      .pipe(map((res) => (typeof res?.data?.message === 'string' ? res.data.message : null)));
  }

  /** `GET /players/requests?box=&status=&page=`: «Todas» (`null`) no envía `status`; `per_page`, nunca. */
  getRequests(query: PlayRequestsQuery): Observable<PlayRequestsResponse> {
    if (!this.isBrowser) return EMPTY;
    let params = new HttpParams().set('box', query.box);
    if (query.status) params = params.set('status', query.status);
    params = params.set('page', String(query.page));
    return this.http.get<PlayRequestsResponse>(`${this.base}/requests`, { params });
  }

  /** `GET /players/requests/summary` → pendientes efectivas recibidas. */
  getSummary(): Observable<number> {
    if (!this.isBrowser) return EMPTY;
    return this.http
      .get<{ data?: { pending_received?: unknown } }>(`${this.base}/requests/summary`)
      .pipe(map((res) => (typeof res?.data?.pending_received === 'number' ? res.data.pending_received : 0)));
  }

  accept(id: number): Observable<PlayRequestActionResult> {
    return this.transition(id, 'accept');
  }

  reject(id: number): Observable<PlayRequestActionResult> {
    return this.transition(id, 'reject');
  }

  cancel(id: number): Observable<PlayRequestActionResult> {
    return this.transition(id, 'cancel');
  }

  close(id: number): Observable<PlayRequestActionResult> {
    return this.transition(id, 'close');
  }

  /** `POST /players/requests/{id}/{acción}` → `{data: solicitud, message}`. */
  private transition(id: number, action: PlayRequestAction): Observable<PlayRequestActionResult> {
    if (!this.isBrowser) return EMPTY;
    return this.http
      .post<{ data: PlayRequestItem; message?: unknown }>(`${this.base}/requests/${id}/${action}`, {})
      .pipe(map((res) => ({ data: res.data, message: typeof res.message === 'string' ? res.message : null })));
  }
}
